import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OfferTextReminderEnum, OfferTextReminderMap } from '@shared/enums';
import { OfferValidator } from '@app/areas/offers/validators';
import { initializeReminder, OfferReminder } from '@app/areas/offers/utils';
import { TimezoneService } from '@app/resources/services/timezone.service';
import { Offer } from '@app/resources/services';
import { BehaviorSubject } from 'rxjs';
import {
  AudienceFilterType,
  AudienceSizeType,
  DateRangePreset,
  RankingMetric,
  WinBackPeriod,
} from '@app/areas/offers/forms/offer-audience-form/offer-audience-filter.types';

export type OfferDateControl = FormControl<OfferDate>;

export enum WhenOfferDateEnum {
  NOW,
  CUSTOM,
}
export interface OfferDate {
  when: WhenOfferDateEnum;
  date?: Date | null;
}

type OfferDetailsFormControls = {
  Name: FormControl<string | null>;
  Location: FormControl<string | null>;
  DiscountPercentage: FormControl<number | null>;
};
type OfferDurationFormControls = {
  StartDate: OfferDateControl;
  EndDate: OfferDateControl;
};
type OfferScheduledSMSFormControls = {
  Reminder: FormControl<OfferReminder>;
  SMSDate: OfferDateControl;
  SMSBody: FormControl<string | null>;
};

type OfferTopConsumersConfigFormControls = {
  RankingMetric: FormControl<RankingMetric>;
  AudienceSizeType: FormControl<AudienceSizeType>;
  CustomCount: FormControl<number | null>;
  DateRangePreset: FormControl<DateRangePreset>;
  DateRangeStart: FormControl<Date | null>;
  DateRangeEnd: FormControl<Date | null>;
};

type OfferWinBackConfigFormControls = {
  InactivityPeriod: FormControl<WinBackPeriod | null>;
};

type OfferTargetAudienceFormControls = {
  FilterType: FormControl<AudienceFilterType | null>;
  IsDynamicAudience: FormControl<boolean>;
  TopConsumersConfig: FormGroup<OfferTopConsumersConfigFormControls>;
  WinBackConfig: FormGroup<OfferWinBackConfigFormControls>;
};

export type OfferDetailsFormGroup = FormGroup<OfferDetailsFormControls>;
export type OfferDurationFormGroup = FormGroup<OfferDurationFormControls>;
export type OfferScheduledSmsFormGroup = FormGroup<OfferScheduledSMSFormControls>;
export type OfferTargetAudienceFormGroup = FormGroup<OfferTargetAudienceFormControls>;

export type OfferFormGroup = FormGroup<{
  Details: OfferDetailsFormGroup;
  Duration: OfferDurationFormGroup;
  ScheduledSMS: OfferScheduledSmsFormGroup;
  TargetAudience: OfferTargetAudienceFormGroup;
}>;

export interface OfferDuration {
  startDate?: Date | string;
  endDate?: Date | string;
}

interface SetControlValueOptions {
  onlySelf?: boolean | undefined;
  emitEvent?: boolean | undefined;
  emitModelToViewChange?: boolean | undefined;
  emitViewToModelChange?: boolean | undefined;
}

@Injectable()
export class OfferFormService {
  private readonly fb = inject(FormBuilder);
  private readonly offerValidator = new OfferValidator(inject(TimezoneService));

  private readonly _previewCount$ = new BehaviorSubject<number | null>(null);
  private readonly _previewLoading$ = new BehaviorSubject<boolean>(false);
  private readonly _previewError$ = new BehaviorSubject<boolean>(false);
  private readonly _previewAverageSpend$ = new BehaviorSubject<number | null>(null);

  readonly previewCount$ = this._previewCount$.asObservable();
  readonly previewLoading$ = this._previewLoading$.asObservable();
  readonly previewError$ = this._previewError$.asObservable();
  readonly previewAverageSpend$ = this._previewAverageSpend$.asObservable();

  setPreviewCount(count: number | null) {
    this._previewCount$.next(count);
  }
  setPreviewLoading(loading: boolean) {
    this._previewLoading$.next(loading);
  }
  setPreviewError(error: boolean) {
    this._previewError$.next(error);
  }
  setPreviewAverageSpend(averageSpend: number | null) {
    this._previewAverageSpend$.next(averageSpend);
  }

  resetPreview() {
    this._previewCount$.next(null);
    this._previewLoading$.next(false);
    this._previewError$.next(false);
    this._previewAverageSpend$.next(null);
  }

  offerForm: OfferFormGroup = this.fb.group({
    Details: this.fb.group<OfferDetailsFormControls>({
      Name: this.fb.control<string | null>(null, {
        validators: [Validators.required],
      }),
      Location: this.fb.control<string | null>(null, {
        validators: [Validators.required],
      }),
      DiscountPercentage: this.fb.control<number | null>(null, {
        validators: [Validators.required, Validators.pattern('[0-9]{1,3}'), Validators.min(0), Validators.max(100)],
      }),
    }),
    Duration: this.fb.group<OfferDurationFormControls>({
      StartDate: this.fb.control<OfferDate>(
        {
          when: WhenOfferDateEnum.CUSTOM,
          date: null,
        } as OfferDate,
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      EndDate: this.fb.control<OfferDate>(
        {
          value: {
            when: WhenOfferDateEnum.CUSTOM,
            date: null,
          },
          disabled: true,
        },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
    }),
    ScheduledSMS: this.fb.group<OfferScheduledSMSFormControls>({
      Reminder: this.fb.control<OfferReminder>(
        {
          key: `${OfferTextReminderEnum.ONE_HOUR_BEFORE}`,
          value: OfferTextReminderMap[OfferTextReminderEnum.ONE_HOUR_BEFORE],
        },
        { nonNullable: true, validators: [Validators.required] }
      ),
      SMSDate: this.fb.control<OfferDate>(
        {
          value: {
            when: WhenOfferDateEnum.CUSTOM,
            date: null,
          },
          disabled: true,
        },
        {
          nonNullable: true,
          validators: [Validators.required],
        }
      ),
      SMSBody: this.fb.control<string | null>(
        { value: null, disabled: true },
        { validators: [Validators.required, Validators.pattern('.{1,160}')] }
      ),
    }),
    TargetAudience: this.fb.group<OfferTargetAudienceFormControls>({
      FilterType: this.fb.control<AudienceFilterType | null>(null),
      IsDynamicAudience: this.fb.control<boolean>(true, { nonNullable: true }),
      TopConsumersConfig: this.fb.group<OfferTopConsumersConfigFormControls>({
        RankingMetric: this.fb.control<RankingMetric>({ value: 'visitCount', disabled: true }, { nonNullable: true }),
        AudienceSizeType: this.fb.control<AudienceSizeType>({ value: 'top10', disabled: true }, { nonNullable: true }),
        CustomCount: this.fb.control<number | null>(
          { value: null, disabled: true },
          { validators: [Validators.min(1)] }
        ),
        DateRangePreset: this.fb.control<DateRangePreset>(
          { value: 'last90days', disabled: true },
          { nonNullable: true }
        ),
        DateRangeStart: this.fb.control<Date | null>({ value: null, disabled: true }),
        DateRangeEnd: this.fb.control<Date | null>({ value: null, disabled: true }),
      }),
      WinBackConfig: this.fb.group<OfferWinBackConfigFormControls>({
        InactivityPeriod: this.fb.control<WinBackPeriod | null>(
          { value: null, disabled: true },
          { validators: [Validators.required] }
        ),
      }),
    }),
  });

  constructor() {
    this.offerForm.controls.TargetAudience.controls.FilterType.valueChanges.subscribe((filterType) => {
      this.syncFilterConfigStates(filterType);
    });
  }

  initializeForm(location: string, offer?: Partial<Offer>) {
    this.resetForm();
    this.offerForm.controls.Duration.controls.EndDate.disable();
    this.offerForm.controls.ScheduledSMS.controls.SMSDate.disable();

    if (offer) {
      this.setOfferDetails(
        { name: offer.namePrivate, location: location, discount: offer.receiveAmount },
        { emitEvent: false }
      );
      this.setOfferDuration(
        {
          startDateTime: {
            when: WhenOfferDateEnum.CUSTOM,
            date: offer.startDateTime ? new Date(offer.startDateTime) : null,
          },
          endDateTime: {
            when: WhenOfferDateEnum.CUSTOM,
            date: offer.endDateTime ? new Date(offer.endDateTime) : null,
          },
        },
        { emitEvent: false }
      );
      this.setOfferScheduledSMS(
        {
          reminder:
            offer.startDateTime && offer.sendTime
              ? initializeReminder({
                  startDateTime: offer.startDateTime,
                  sendTime: offer.sendTime,
                })
              : null,
          smsDate: {
            when: WhenOfferDateEnum.CUSTOM,
            date: offer.sendTime ? new Date(offer.sendTime) : null,
          },
          smsBody: JSON.parse(offer.display ?? `{"en-US": ""}`)['en-US'],
        },
        { emitEvent: false }
      );
      const audienceFilterType = offer.audienceFilter?.filterType ?? null;
      const cfg = offer.audienceFilter?.config as any;
      this.setTargetAudience(
        {
          filterType: audienceFilterType,
          isDynamicAudience: offer.isDynamicAudience ?? false,
          topConsumersConfig:
            audienceFilterType === AudienceFilterType.TopConsumers && cfg
              ? {
                  rankingMetric: cfg.rankingMetric ?? 'visitCount',
                  audienceSizeType: cfg.audienceSizeType ?? undefined,
                  customCount: cfg.customCount ?? null,
                  dateRangePreset: cfg.dateRangePreset ?? undefined,
                  dateRangeStart: cfg.dateRangeStart ?? null,
                  dateRangeEnd: cfg.dateRangeEnd ?? null,
                }
              : undefined,
          winBackConfig:
            audienceFilterType === AudienceFilterType.WinBack && cfg
              ? {
                  inactivityPeriod: cfg.inactivityPeriod ?? null,
                }
              : undefined,
        },
        { emitEvent: false }
      );
    } else this.setOfferDetails({ location: location }, { emitEvent: false });
    this.offerForm.markAsPristine();
    this.offerForm.markAsUntouched();
    this.setAsyncValidators();
  }

  public setAsyncValidators() {
    this.clearAsyncValidators();
    this.offerForm.controls.Duration.controls.StartDate.setAsyncValidators([
      this.offerValidator.offerDurationStartDateValidatorAsync(),
    ]);

    this.offerForm.controls.Duration.controls.EndDate.setAsyncValidators([
      this.offerValidator.offerDurationEndDateValidatorAsync(this.offerForm.controls.Duration.controls.StartDate),
    ]);

    this.offerForm.controls.ScheduledSMS.controls.SMSDate.setAsyncValidators([
      this.offerValidator.offerSMSDateValidatorAsync(this.offerForm.controls.Duration.controls.StartDate),
    ]);

    this.offerForm.controls.ScheduledSMS.controls.Reminder.setAsyncValidators([
      this.offerValidator.offerSMSReminderValidatorAsync(this.offerForm.controls.Duration.controls.StartDate),
    ]);
  }

  private clearAsyncValidators() {
    for (const field in this.offerForm.controls) {
      const control = this.offerForm.get(field);
      if (control) {
        for (const subField in (control as FormGroup).controls) {
          if (subField === 'Name') continue;
          const subControl = (control as FormGroup).get(subField);
          subControl?.clearAsyncValidators();
        }
      }
    }
  }

  disableForm(disable: boolean) {
    if (disable) {
      this.offerForm.disable();
    } else {
      this.offerForm.enable();
      this.syncFilterConfigStates(this.offerForm.controls.TargetAudience.controls.FilterType.value);
    }
  }

  /**
   * Locks the whole audience section (filter, config, and list type). The audience/list is a
   * creation-time decision — it cannot be changed once the offer exists, because the frozen
   * snapshot is already materialized and a dynamic list is re-resolved at send.
   */
  lockTargetAudience() {
    this.offerForm.controls.TargetAudience.disable({ emitEvent: false });
  }

  setOfferDetails(data: { name?: string; location: string; discount?: number }, options?: SetControlValueOptions) {
    if (data.name) this.getOfferDetails(this.offerForm).controls.Name.setValue(data.name, options);
    if (data.location) this.getOfferDetails(this.offerForm).controls.Location.setValue(data.location, options);
    if (data.discount)
      this.getOfferDetails(this.offerForm).controls.DiscountPercentage.setValue(data.discount, options);
  }

  setOfferDuration(data: { startDateTime?: OfferDate; endDateTime?: OfferDate }, options?: SetControlValueOptions) {
    if (data.startDateTime)
      this.getOfferDuration(this.offerForm).controls.StartDate.setValue(data.startDateTime, options);
    if (data.endDateTime) this.getOfferDuration(this.offerForm).controls.EndDate.setValue(data.endDateTime, options);
  }

  setOfferScheduledSMS(
    data: {
      reminder?: OfferReminder | null;
      smsDate?: OfferDate;
      smsBody?: string | null;
      saveAsTemplate?: boolean | null;
    },
    options?: SetControlValueOptions
  ) {
    if (data.reminder) this.getOfferScheduledSMS(this.offerForm).controls.Reminder.setValue(data.reminder, options);
    if (data.smsDate) {
      this.getOfferScheduledSMS(this.offerForm).controls.SMSDate.setValue(data.smsDate, options);
    }
    if (data.smsBody) this.getOfferScheduledSMS(this.offerForm).controls.SMSBody.setValue(data.smsBody, options);
  }

  getOfferForm() {
    return this.offerForm;
  }

  getOfferDetails(offerForm: OfferFormGroup) {
    return offerForm.controls.Details;
  }

  getOfferDuration(offerForm: OfferFormGroup) {
    return offerForm.controls.Duration;
  }

  getOfferScheduledSMS(offerForm: OfferFormGroup) {
    return offerForm.controls.ScheduledSMS;
  }

  getTargetAudience(offerForm: OfferFormGroup) {
    return offerForm.controls.TargetAudience;
  }

  setTargetAudience(
    data: {
      filterType?: AudienceFilterType | null;
      isDynamicAudience?: boolean | null;
      topConsumersConfig?: Partial<{
        rankingMetric: RankingMetric | null;
        audienceSizeType: AudienceSizeType | null;
        customCount: number | null;
        dateRangePreset: DateRangePreset | null;
        dateRangeStart: Date | null;
        dateRangeEnd: Date | null;
      }>;
      winBackConfig?: Partial<{
        inactivityPeriod: WinBackPeriod | null;
      }>;
    },
    options?: SetControlValueOptions
  ) {
    if (data.filterType !== undefined) {
      const newFilterType = data.filterType ?? null;
      this.getTargetAudience(this.offerForm).controls.FilterType.setValue(newFilterType, options);
      // Sync enabled state explicitly — valueChanges won't fire when emitEvent is false
      this.syncFilterConfigStates(newFilterType);
    }
    if (data.isDynamicAudience !== undefined) {
      this.getTargetAudience(this.offerForm).controls.IsDynamicAudience.setValue(
        data.isDynamicAudience ?? false,
        options
      );
    }
    if (data.topConsumersConfig) {
      const topConsumersGroup = this.getTargetAudience(this.offerForm).controls.TopConsumersConfig;
      if (data.topConsumersConfig.rankingMetric !== undefined) {
        topConsumersGroup.controls.RankingMetric.setValue(
          data.topConsumersConfig.rankingMetric ?? 'visitCount',
          options
        );
      }
      if (data.topConsumersConfig.audienceSizeType !== undefined) {
        topConsumersGroup.controls.AudienceSizeType.setValue(
          data.topConsumersConfig.audienceSizeType ?? 'top10',
          options
        );
      }
      if (data.topConsumersConfig.customCount !== undefined) {
        topConsumersGroup.controls.CustomCount.setValue(data.topConsumersConfig.customCount ?? null, options);
      }
      if (data.topConsumersConfig.dateRangePreset !== undefined) {
        topConsumersGroup.controls.DateRangePreset.setValue(
          data.topConsumersConfig.dateRangePreset ?? 'last90days',
          options
        );
      }
      if (data.topConsumersConfig.dateRangeStart !== undefined) {
        topConsumersGroup.controls.DateRangeStart.setValue(data.topConsumersConfig.dateRangeStart ?? null, options);
      }
      if (data.topConsumersConfig.dateRangeEnd !== undefined) {
        topConsumersGroup.controls.DateRangeEnd.setValue(data.topConsumersConfig.dateRangeEnd ?? null, options);
      }
    }
    if (data.winBackConfig) {
      const winBackGroup = this.getTargetAudience(this.offerForm).controls.WinBackConfig;
      if (data.winBackConfig.inactivityPeriod !== undefined) {
        winBackGroup.controls.InactivityPeriod.setValue(data.winBackConfig.inactivityPeriod ?? null, options);
      }
    }
  }

  private syncFilterConfigStates(filterType: AudienceFilterType | null) {
    const ta = this.offerForm.controls.TargetAudience.controls;

    // TopConsumers
    if (filterType === AudienceFilterType.TopConsumers) {
      ta.TopConsumersConfig.enable({ emitEvent: false });
    } else {
      ta.TopConsumersConfig.disable({ emitEvent: false });
      ta.TopConsumersConfig.reset(
        {
          RankingMetric: 'visitCount',
          AudienceSizeType: 'top10',
          CustomCount: null,
          DateRangePreset: 'last90days',
          DateRangeStart: null,
          DateRangeEnd: null,
        },
        { emitEvent: false }
      );
    }

    // WinBack
    if (filterType === AudienceFilterType.WinBack) {
      ta.WinBackConfig.enable({ emitEvent: false });
    } else {
      ta.WinBackConfig.disable({ emitEvent: false });
      ta.WinBackConfig.reset({ InactivityPeriod: null }, { emitEvent: false });
    }
  }

  resetForm() {
    this.offerForm.reset({}, { emitEvent: false });
    this.syncFilterConfigStates(null);
    // "Recalculate before sending" is on by default for a new offer.
    this.offerForm.controls.TargetAudience.controls.IsDynamicAudience.setValue(true, { emitEvent: false });
  }
}
